import { ActorRefFrom, InterpreterFrom, StateMachine } from "xstate"
import { CruiseCode } from "@ssc-booking/functional-support"
import { UUID } from "@ssc-booking/functional-support/src/uuid"
import { PostUserEventsRequest } from "@silversea-ssc/dotcom-api-types/UserEvents"
import { useActor } from "@xstate/react"
import { SuiteNumber } from "@ssc-booking/api-contracts"
import * as Occupancy from "@ssc-booking/api-contracts/src/occupancy"
import { FareCard } from "../read-models/fare-card"
import { Ev } from "../../../_common/machines/event"
import type { ECommerceEvent, ECommerceState } from "./ecommerce-machine/e-commerce-machine"
import {
    CheckoutState,
    FareCardRef,
    SuiteCategorySource,
    VsCodeState,
} from "./ecommerce-machine/e-commerce-machine-context"

type InitialUserEventsContext = {
    sessionId: UUID
    restoreCompleted: boolean
    cruiseCode: CruiseCode
    guests: Occupancy.Occupancy
    checkoutStatus: CheckoutState
    vsCode: VsCodeState
    fareCards: readonly FareCard[]
    selectedFare?: FareCardRef
    selectedSuiteCategory?: string
    selectedSuiteCategorySource?: SuiteCategorySource
    selectedSuiteNumber?: SuiteNumber
    eventToSend?: PostUserEventsRequest
}

type EnabledContext = InitialUserEventsContext & {
    userId: string
}

export type UserEventsTypeState =
    | { value: "disabled"; context: InitialUserEventsContext }
    | { value: "enabled"; context: EnabledContext }

export type UserEventsContext = InitialUserEventsContext & Partial<EnabledContext>
export type UserEventsEvents = ECommerceEvent | Ev<"SET_USER_ID", { userId: string }> | Ev<"RESET_USER_ID">

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UserEventsStateMachine = StateMachine<UserEventsContext, any, UserEventsEvents, UserEventsTypeState>
type UserEventsInterpreter = InterpreterFrom<UserEventsStateMachine>
export type UserEventsState = UserEventsInterpreter["state"]
export type UserEventsSend = UserEventsInterpreter["send"]
type UserEventsActorRef = ActorRefFrom<UserEventsStateMachine>
export type UserEventsTypes = {
    Context: UserEventsContext
    Events: UserEventsEvents
    TypeState: UserEventsTypeState
    StateMachine: UserEventsStateMachine
    Interpreter: UserEventsInterpreter
    State: UserEventsState
    Send: UserEventsSend
    ActorRef: UserEventsActorRef
}

export type SendUserEvent = (event: PostUserEventsRequest) => Promise<void>
export type WindowLocation = () => URL

export const useUserEvents = (state: ECommerceState): [UserEventsState, UserEventsSend] => {
    const machine = state.children.userEvents as UserEventsActorRef
    return useActor(machine) as unknown as [UserEventsState, UserEventsSend]
}
